import {
    faArrowCircleLeft,
    faArrowCircleRight,
    faCheck,
    faCheckCircle,
    faEdit,
    faQuestion,
    faRedo,
    faSortNumericUp,
    faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { faInfoCircle, faTrain } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button, Input, Modal, notification, Popconfirm, Popover, Select, Slider, Switch, Tooltip } from "antd";
import OBCAutoIndicator from "components/OBC/OBCAutoIndicator";
import AssetIDHelper from "components/util/AssetIDHelper";
import OBCSpinner from "components/util/OBC";
import { formatHumanDate } from "components/util/TimezoneUtils";
import ZoomableImage from "components/ZoomableImage";
import _ from "lodash";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    getIssueObservations,
    goToBounds,
    refreshAssetObservations,
    reviewIssueObservation,
    routeSelected,
    selectAsset,
    selectIssue,
    selectObservation,
    setAssetObservationClassification,
    setAssetUnreadable,
    updateIssue,
    updateIssueObservationClassification,
} from "redux/actions";
import { currentDashboardSelector } from "redux/selectors";
import { combineGroundTruthConsensus, createGroundTruthFields, StatusElement } from "./issueUtils";
import { validateGroundTruth } from "./groundTruthUtils";

const IssueReviewStatuses = {
    3: "rejected",
    4: "unmatched",
    5: "matched",
};

const ohlVegetationOptions = {
    ok: "Acceptable vegetation observed",
    vegetation_around_base: "Vegetation around the base",
    bad: "Unacceptable vegetation observed",
};

const classificationTypeIcons = {
    Yes: { icon: faCheck, style: { color: "#479ff8" } },
    No: { icon: faTimes, style: { color: "#479ff8", fontSize: 18 } },
    Uncertain: { icon: faQuestion, style: { color: "lightgrey" } },
    Unknown: { icon: faQuestion, style: { color: "lightgrey" } },
};

const confidenceConfigs = {
    MAX: { colour: "#47C66B", percent: "100%" },
    HIGH: { colour: "#47C66B", percent: "70%" },
    LOW: { colour: "#E55151", percent: "20%" },
    UNDEFINED: { colour: "#FFFFFF", percent: "0%" },
};

const PopoverButton = ({ onClick, buttonText, popoverText, type, disabled }) => {
    const [open, setOpen] = useState(false);

    return (
        <Popconfirm
            placement="topLeft"
            title={"Are you sure?"}
            onConfirm={onClick}
            okText="Yes"
            cancelText="No">
            <Tooltip
                placement="topRight"
                title={popoverText}
                visible={open}>
                <Button
                    type={type}
                    className={`WarnButton ${disabled ? "Disabled" : ""}`}
                    onClick={() => setOpen(false)}
                    onMouseEnter={() => setOpen(true)}
                    onMouseLeave={() => setOpen(false)}>
                    {buttonText}
                    <FontAwesomeIcon
                        icon={faInfoCircle}
                        className={"ButtonIcon"}
                    />
                </Button>
            </Tooltip>
        </Popconfirm>
    );
};

const issuesSelector = (state) => state.issues.data;
const assetsSelector = (state) => state.mlAssets.assetData;
const selectedIssueDataSelector = (state) => state.issues.selected;
const selectedAssetDataSelector = (state) => state.mlAssets.selected;
const superuserSelector = (state) => state.userDetails.userConfig.super_admin;
const convertToUtcSelector = (state) => state.userDetails.userConfig.convert_to_utc;
const assetIDsSelector = (state) => state.mlAssets.ids;
const filteredIssuesSelector = (state) => state.issues.filteredIssues;
const csrfTokenSelector = (state) => state.csrfToken;
const objectObservationDataTypesSelector = (state) => state.objectObservationDataTypes;

const MINUTE_IN_MS = 60000;

const resetZoom = (prevZoom) => {
    if (prevZoom !== 1) {
        return 1;
    }

    return prevZoom;
};

const focusModal = () => {
    setTimeout(() => {
        const modal = document.querySelector(".ant-modal.ObservationModal");
        if (modal) modal.firstChild.focus();
    });
};

const ObservationModal = ({ closeModal, observations, isIssue, unreadableUpdated, setUnreadableUpdated, displayUnreadable }) => {
    const dispatch = useDispatch();

    const [brightness, setBrightness] = useState(0);
    const [contrast, setContrast] = useState(0);
    const [zoom, setZoom] = useState(1);
    const [editingFieldKey, setEditingFieldKey] = useState();
    const [editingFieldValue, setEditingFieldValue] = useState();
    const [groundTruthError, setGroundTruthError] = useState();
    const [displayReadableModal, setDisplayReadableModal] = useState(false);
    const [loadingNextAsset, setLoadingNextAsset] = useState(false);
    const [showImageOverlay, setShowImageOverlay] = useState(true);
    const [successfullyRefetchedObservations, setSuccessfullyRefetchedObservations] = useState(false);
    const [editingOcr, setEditingOcr] = useState(false);
    const [ocrOverride, setOcrOverride] = useState("");
    const [ocrOverrideError, setOcrOverrideError] = useState("");

    const issues = useSelector(issuesSelector);
    const assets = useSelector(assetsSelector);
    const selectedIssueData = useSelector(selectedIssueDataSelector);
    const selectedAssetData = useSelector(selectedAssetDataSelector);
    const superuser = useSelector(superuserSelector);
    const convertToUtc = useSelector(convertToUtcSelector);
    const assetIDs = useSelector(assetIDsSelector);
    const filteredIssues = useSelector(filteredIssuesSelector);
    const csrfToken = useSelector(csrfTokenSelector);
    const currentDashboard = useSelector(currentDashboardSelector);
    const objectObservationDataTypes = useSelector(objectObservationDataTypesSelector);

    const shouldShowVeg = _.get(currentDashboard, ["config", "show_ohl_veg"], true);

    const groundTruthInputRef = useRef();
    const ocrInputRef = useRef();

    const observation = useMemo(() => {
        if (!observations || !observations.length) {
            return {};
        }

        if (isIssue) {
            const selectedObservation = _.find(observations, { id: selectedIssueData.observation });
            if (selectedObservation) {
                return selectedObservation;
            }
        } else {
            const selectedObservation = _.find(observations, { id: selectedAssetData.observation });
            if (selectedObservation) {
                return selectedObservation;
            }
        }
        return {};
    }, [isIssue, observations, selectedAssetData, selectedIssueData]);

    const bottomOutTemperature = useMemo(() => {
        if (!isIssue) {
            let assetType = _.get(_.find(assets, { id: selectedAssetData.asset }), "class");
            if (assetType !== "Tensioner") {
                return undefined;
            }
            return _.get(observation, "bottom_out_temperature", null);
        }
        return undefined;
    }, [observation, assets, selectedAssetData, isIssue]);

    const humanDate = useMemo(() => {
        if (observation.timestamp) {
            return formatHumanDate(observation.timestamp, convertToUtc);
        }
    }, [observation, convertToUtc]);

    const orderedObservations = useMemo(() => {
        return _.orderBy(observations, "timestamp", "desc");
    }, [observations]);

    const currentIndex = useMemo(() => {
        return _.findIndex(orderedObservations, { id: selectedAssetData.observation });
    }, [orderedObservations, selectedAssetData.observation]);

    const navigate = useCallback(
        (forward) => {
            if (forward) {
                const nextObservation = orderedObservations[currentIndex + 1];
                dispatch(selectObservation(nextObservation.id));
            } else {
                const nextObservation = orderedObservations[currentIndex - 1];
                dispatch(selectObservation(nextObservation.id));
            }
            if (!showImageOverlay) {
                setShowImageOverlay(true);
            }
            setZoom(resetZoom);
        },
        [currentIndex, dispatch, orderedObservations, showImageOverlay],
    );

    const currentIssue = useMemo(() => {
        if (isIssue) {
            return _.find(issues, { id: selectedIssueData.issue });
        } else {
            return assets[selectedAssetData.asset];
        }
    }, [assets, isIssue, issues, selectedAssetData.asset, selectedIssueData.issue]);

    const onEditClick = useCallback(
        (fieldKey, fieldValue) => {
            setEditingFieldKey(fieldKey);

            let value = fieldValue;
            if (value === "Unknown") {
                value = "";
            }
            if (fieldKey === "Midpoint Distance") {
                value = _.get(currentIssue, ["ground_truth_data", "midpoint_distance_meters"], "");
            } else {
                value = new AssetIDHelper(value).csv_to_slashes();
            }
            setEditingFieldValue(value);
            setTimeout(() => {
                if (groundTruthInputRef && groundTruthInputRef.current) {
                    groundTruthInputRef.current.focus();
                }
            }, 100);
        },
        [currentIssue],
    );

    const saveGroundTruthUpdate = useCallback(
        (fieldKey) => {
            const newGroundTruthObj = _.cloneDeep(currentIssue.ground_truth_data);
            newGroundTruthObj[fieldKey] = new AssetIDHelper(editingFieldValue).slashes_and_hyphens_to_csv();
            const newGroundTruthString = JSON.stringify(newGroundTruthObj);
            dispatch(
                updateIssue(
                    currentIssue.id,
                    {
                        ground_truth_data: newGroundTruthString,
                    },
                    false,
                ),
            );
            setEditingFieldKey(null);
            setEditingFieldValue(null);
            focusModal();
        },
        [currentIssue, dispatch, editingFieldValue],
    );

    const saveMidpointDistanceUpdate = useCallback(
        (fieldKey) => {
            let newGroundTruthObj = _.cloneDeep(currentIssue.ground_truth_data);
            // if value has been cleared then remove midpoint_distance_meters key from the object
            if (editingFieldValue.length) {
                newGroundTruthObj["midpoint_distance_meters"] = Number(editingFieldValue);
            } else {
                newGroundTruthObj = _.omit(newGroundTruthObj, "midpoint_distance_meters");
            }
            const newGroundTruthString = JSON.stringify(newGroundTruthObj);
            dispatch(
                updateIssue(
                    currentIssue.id,
                    {
                        ground_truth_data: newGroundTruthString,
                    },
                    isIssue,
                    true,
                ),
            );
            setEditingFieldKey(null);
            setEditingFieldValue(null);
        },
        [currentIssue, dispatch, editingFieldValue, isIssue],
    );

    const formatImage = (imgUrl) => {
        // remove frame index if session is from before 01/05/2020
        if (Math.round(observation.timestamp / 1000) <= 1588287599) {
            const arr = imgUrl.split("");
            const lastSlash = _.findLastIndex(arr, (e) => e === "/");
            const lastIndex = _.findLastIndex(arr, (e) => e === "-");
            if (lastIndex > lastSlash) {
                return arr.slice(0, lastIndex).join("") + ".jpg";
            }
        }

        if (csrfToken) {
            imgUrl += `?csrf=${csrfToken}`;
        }
        return imgUrl;
    };

    const onEditingFieldChange = (fieldKey, value) => {
        const regexError = validateGroundTruth(fieldKey, value);
        let newValue = value;
        if (regexError) {
            setGroundTruthError(regexError);
        } else {
            setGroundTruthError(null);
        }

        if (fieldKey === "Midpoint Distance") {
            newValue = value.replace(/[^0-9.]+/g, "").replace(/^(\d+\.\d{0,2}).*/g, "$1");
        } else {
            // if Signal ID or Structural ID
            newValue = new AssetIDHelper(value).csv_to_slashes();
        }
        setEditingFieldValue(newValue);
    };

    const [prevAssetData, nextAssetData, currentAssetIndex, totalAssets] = useMemo(() => {
        if (!_.get(currentIssue, "id")) return [false, false, 0, 0];

        const issueOrAssetArray = isIssue ? filteredIssues : assetIDs;
        const predicate = isIssue ? (issue) => issue.id === currentIssue.id : (asset) => asset[0] === currentIssue.id;

        const currentIndex = _.findIndex(issueOrAssetArray, predicate);
        const prevAsset = _.get(issueOrAssetArray, [currentIndex - 1], false);
        const nextAsset = _.get(issueOrAssetArray, [currentIndex + 1], false);

        return [prevAsset, nextAsset, currentIndex, issueOrAssetArray.length];
    }, [assetIDs, currentIssue, filteredIssues, isIssue]);

    const issueInfo = useMemo(() => {
        const infoItems = [];
        if (_.get(currentIssue, ["summary_data", "match_count"], 0)) {
            infoItems.push(
                <div
                    className="InfoItem"
                    key="current-item">
                    <div className="TitleContainer">
                        <p className="Title">{isIssue ? "Issue" : "Asset"} Item</p>
                    </div>
                    <div className="InfoValues">
                        <FontAwesomeIcon
                            className="InfoIcon"
                            icon={faSortNumericUp}
                        />
                        <p className="InfoText">
                            {/* {_.findIndex(assetIDs, (a) => a[0] === selectedAssetData.asset) + 1} of {assetIDs.length} */}
                            {currentAssetIndex + 1} of {totalAssets}
                        </p>
                    </div>
                </div>,
            );
        }

        // ground truth for Assets
        if (currentIssue && currentIssue.ground_truth_data && currentIssue.consensus_data) {
            const issuePropertyData = superuser
                ? combineGroundTruthConsensus(currentIssue.ground_truth_data, currentIssue.consensus_data, currentIssue.class)
                : createGroundTruthFields(currentIssue.ground_truth_data, currentIssue.consensus_data, currentIssue.class);

            console.log("debug issuePropertyData", issuePropertyData);

            if (!_.isEmpty(issuePropertyData)) {
                issuePropertyData.forEach((property) => {
                    let value = property.value;

                    // replace the way we display Signal and "Structure IDs
                    if (["Signal ID", "Structure ID"].includes(property.name)) {
                        value = new AssetIDHelper(property.value).csv_to_slashes();
                    }
                    // do not display too many digits after decimal point for coherence
                    else if (property.name === "Coherence") {
                        value = String(_.floor(parseInt(property.value), 2)) + "%";
                    }

                    let valueElement = (
                        <>
                            <FontAwesomeIcon
                                className="InfoIcon"
                                icon={property.icon}
                            />
                            <p className="InfoText">{value}</p>
                            <StatusElement
                                status={property.status}
                                issue={currentIssue}
                                groundTruthKey={property.fieldName}
                                onVerify={focusModal}
                            />
                        </>
                    );
                    let editIcon = faEdit;
                    let onIconClick = () => onEditClick(property.fieldName, property.value);

                    if (property.fieldName === editingFieldKey) {
                        valueElement = (
                            <Input.Search
                                ref={groundTruthInputRef}
                                size="medium"
                                onSearch={() => saveGroundTruthUpdate(property.fieldName)}
                                className="InfoValueInput"
                                value={editingFieldValue}
                                onChange={(e) => onEditingFieldChange(property.fieldName, e.target.value)}
                                enterButton={
                                    <Button
                                        disabled={!!groundTruthError}
                                        type="primary"
                                        onClick={() => saveGroundTruthUpdate(property.fieldName)}>
                                        Save
                                    </Button>
                                }
                            />
                        );
                        editIcon = faTimes;
                        onIconClick = () => {
                            setEditingFieldKey(null);
                            setEditingFieldValue(null);
                            setGroundTruthError(null);
                        };
                    }
                    infoItems.push(
                        <div
                            className="InfoItem"
                            key={_.kebabCase(property.name)}>
                            <div className="TitleContainer">
                                <p className="Title">{property.name}</p>
                                {property.status && (
                                    <FontAwesomeIcon
                                        className="EditIcon"
                                        icon={editIcon}
                                        onClick={onIconClick}
                                    />
                                )}
                            </div>
                            <div className="InfoValues">{valueElement}</div>
                        </div>,
                    );
                });
            }
        }

        if (currentIssue && currentIssue.class === "Signal") {
            const classificationTypes = _.get(objectObservationDataTypes, "Signal", {});

            const fields = _.get(currentIssue, ["summary_data", "fields"], {});

            const classifications = [];

            Object.keys(classificationTypes).forEach((key) => {
                if (key.includes("isolation") && !_.get(currentDashboard, ["config", "show_essow_input"], false)) {
                    return;
                }

                const field = _.get(fields, key, "uncertain");

                const classification = _.get(classificationTypes, [key, "data_values", field, "display_name"], "Unknown");
                const { icon, style } = classificationTypeIcons[classification];

                const confidenceValue = _.get(fields, _.get({ signal_ladder: "sl_conf", signal_screen: "ss_conf" }, key, null), "UNDEFINED");
                const confidence = _.get(confidenceConfigs, confidenceValue, null);

                classifications.push(
                    <div
                        key={key}
                        className="Classification">
                        <strong>{_.get(classificationTypes, [key, "display_name"], "Unknown")}</strong>
                        <div className="Body">
                            <FontAwesomeIcon
                                style={{ fontSize: 16, ...style }}
                                icon={icon}
                            />
                            {confidence !== null && superuser && (
                                <Tooltip
                                    title={`Confidence: ${confidenceValue}`}
                                    placement="bottom">
                                    <div className="Confidence">
                                        <div style={{ width: 6, backgroundColor: confidence.colour, height: confidence.percent }}></div>
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    </div>,
                );
            });

            if (classifications.length) {
                infoItems.push(
                    <div
                        className="InfoItem"
                        key="signal-classifications">
                        <div className="ClassificationsSummary">{classifications}</div>
                    </div>,
                );
            }
        }

        // Enable ground truth input field for Issues and Assets
        if (currentIssue && currentIssue.class === "Tensioner" && currentIssue.ground_truth_data) {
            let editIcon = faEdit;
            const balanceWeightStructureID = _.get(currentIssue, ["ground_truth_data", "structure_id"], "");
            let onIconClick = () => onEditClick("Structure ID", balanceWeightStructureID);

            let structureIDValueElement = <p className="InfoText">{balanceWeightStructureID}</p>;

            if (editingFieldKey === "Structure ID") {
                structureIDValueElement = (
                    <div>
                        <Input
                            ref={groundTruthInputRef}
                            size="medium"
                            style={{ width: "160px" }}
                            className="InfoValueInput"
                            value={editingFieldValue}
                            onChange={(e) => onEditingFieldChange("structure_id", e.target.value)}
                        />
                        <Button
                            type="primary"
                            disabled={!!groundTruthError}
                            onClick={() => saveGroundTruthUpdate("structure_id")}>
                            Save
                        </Button>
                    </div>
                );

                editIcon = faTimes;
                onIconClick = () => {
                    setEditingFieldKey(null);
                    setEditingFieldValue(null);
                    setGroundTruthError(null);
                };
            }

            infoItems.push(
                <div
                    className="InfoItem"
                    key="structure-id">
                    <div className="TitleContainer">
                        <p className="Title">Structure ID</p>
                        <FontAwesomeIcon
                            className="EditIcon"
                            icon={editIcon}
                            onClick={onIconClick}
                        />
                    </div>
                    <div className="InfoValues">{structureIDValueElement}</div>
                </div>,
            );

            let issueMPD;

            if (observation.class_specific_data) {
                issueMPD = _.get(observation, ["class_specific_data", "estimated_mp_distance"]);
            } else {
                issueMPD = _.get(observation, ["observation_info", "estimated_mp_distance"]);
            }

            let valueElement;
            if (issueMPD && !currentIssue.ground_truth_data?.midpoint_distance_meters) {
                valueElement = (
                    <>
                        <p className="InfoText">{issueMPD + "m (MPD Estimated Automatically)"}</p>
                    </>
                );
            } else if (issueMPD && currentIssue.ground_truth_data?.midpoint_distance_meters) {
                valueElement = (
                    <>
                        <p className="InfoText">{currentIssue.ground_truth_data.midpoint_distance_meters + "m"}</p>
                    </>
                );
            }

            onIconClick = () => onEditClick("Midpoint Distance", issueMPD ?? "");
            editIcon = faEdit;

            if ("Midpoint Distance" === editingFieldKey) {
                valueElement = (
                    <Input.Search
                        ref={groundTruthInputRef}
                        size="medium"
                        style={{ width: "220px" }}
                        onSearch={() => saveMidpointDistanceUpdate("Midpoint Distance")}
                        className="InfoValueInput"
                        value={editingFieldValue}
                        onChange={(e) => onEditingFieldChange("Midpoint Distance", e.target.value)}
                        suffix={"meters"}
                        maxLength={8}
                        enterButton={
                            <Button
                                disabled={!!groundTruthError}
                                type="primary"
                                onClick={() => saveMidpointDistanceUpdate("Midpoint Distance")}>
                                Save
                            </Button>
                        }
                    />
                );
                editIcon = faTimes;
                onIconClick = () => {
                    setEditingFieldKey(null);
                    setEditingFieldValue(null);
                    setGroundTruthError(null);
                };
            }
            infoItems.push(
                <div
                    className="InfoItem"
                    key="midpoint-distance">
                    <div className="TitleContainer">
                        <p className="Title">{"Midpoint Distance"}</p>
                        {true && (
                            <FontAwesomeIcon
                                className="EditIcon"
                                icon={editIcon}
                                onClick={onIconClick}
                            />
                        )}
                    </div>
                    <div className="InfoValues">{valueElement}</div>
                </div>,
            );
        }

        if (bottomOutTemperature !== undefined) {
            infoItems.push(
                <div
                    className="InfoItem"
                    key="bottom-out-temperature">
                    <div className="TitleContainer">
                        <p className="Title">Bottom Out Temperature</p>
                    </div>
                    <div className="InfoValues">{bottomOutTemperature || bottomOutTemperature === 0 ? `${bottomOutTemperature}°C` : ""}</div>
                </div>,
            );
        }

        return infoItems;
    }, [
        currentIssue,
        bottomOutTemperature,
        isIssue,
        currentAssetIndex,
        totalAssets,
        superuser,
        editingFieldKey,
        onEditClick,
        editingFieldValue,
        groundTruthError,
        saveGroundTruthUpdate,
        objectObservationDataTypes,
        currentDashboard,
        observation,
        saveMidpointDistanceUpdate,
    ]);

    const openSession = () => {
        console.log("debug observation", observation);
        if (observation.rail_image_timestamp) {
            const railInspectionLink = `/rail-inspection/${observation.session_id}/${observation.timestamp}`;
            window.open(railInspectionLink);
        } else {
            const timeToJumpTo = observation.timestamp / 1000 || observation.video_key || observation.rail_image_timestamp / 1000;
            dispatch(routeSelected(observation.session_id, timeToJumpTo));
        }
        closeModal();
    };

    const reviewObservation = (reviewStatus) => {
        if (observations.length === 1 && _.includes([3, 4], reviewStatus) && isIssue) {
            dispatch(updateIssue(currentIssue.id, { state: 2 }, isIssue));
        }

        const callback = (newObservations) => {
            notification.success({
                message: `Successfully ${IssueReviewStatuses[reviewStatus] ?? "reviewed"} observation.`,
            });

            if (reviewStatus !== 5) {
                const newObservationsLength = isIssue ? newObservations.length : newObservations.observations.length;

                if (newObservationsLength === 0) {
                    return closeModal();
                }

                if (currentIndex < newObservationsLength) {
                    navigate(true);
                } else if (currentIndex > 0) {
                    navigate(false);
                }
            }
        };

        dispatch(reviewIssueObservation(observation.id, reviewStatus, observation.observation_type, currentIssue.id, isIssue, callback));
    };

    const goToAssetID = useCallback(
        (assetData) => {
            if (assetData) {
                setZoom(resetZoom);
                setLoadingNextAsset(true);

                if (isIssue) {
                    const issueID = _.get(assetData, "id");
                    dispatch(selectIssue(issueID));
                    dispatch(getIssueObservations(issueID)).then((observations) => {
                        const orderedObservations = _.orderBy(observations, "timestamp", "desc");
                        let observationID = _.get(orderedObservations, [0, "id"], false);

                        let north = _.get(assetData, ["coords", [1]], 0) + 0.002;
                        let east = _.get(assetData, ["coords", [0]], 0) + 0.002;
                        let south = _.get(assetData, ["coords", [1]], 0) - 0.002;
                        let west = _.get(assetData, ["coords", [0]], 0) - 0.002;

                        if (observationID) {
                            dispatch(selectObservation(observationID));
                            dispatch(
                                goToBounds({
                                    north,
                                    south,
                                    east,
                                    west,
                                }),
                            );
                        }
                        setLoadingNextAsset(false);
                    });
                } else {
                    const assetID = _.get(assetData, [0]);
                    dispatch(selectAsset(assetID)).then((observations) => {
                        const orderedObservations = {
                            observations: _.orderBy(observations.observations, "timestamp", "desc"),
                            external_observations: _.orderBy(observations.external_observations, "timestamp", "desc"),
                        };
                        let observationID =
                            _.get(orderedObservations, ["observations", 0, "id"], false) ||
                            _.get(orderedObservations, ["external_observations", 0, "id"], false);

                        let north = _.get(assetData, [[1], [1]], 0) + 0.002;
                        let east = _.get(assetData, [[1], [0]], 0) + 0.002;
                        let south = _.get(assetData, [[1], [1]], 0) - 0.002;
                        let west = _.get(assetData, [[1], [0]], 0) - 0.002;

                        if (observationID) {
                            dispatch(selectObservation(observationID));
                            dispatch(
                                goToBounds({
                                    north,
                                    south,
                                    east,
                                    west,
                                }),
                            );
                        }
                        setLoadingNextAsset(false);
                    });
                }
            }
        },
        [dispatch, isIssue],
    );

    const navigateAsset = useCallback(
        (next = true) => {
            if (loadingNextAsset) return;

            goToAssetID(next ? nextAssetData : prevAssetData);
        },
        [goToAssetID, loadingNextAsset, nextAssetData, prevAssetData],
    );

    const handleKeyPress = useCallback(
        (event) => {
            const el = _.get(event, ["target"], {});
            if (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable) return;

            switch (event.key) {
                case "ArrowLeft":
                    if (currentIndex > 0) {
                        navigate(false);
                    }
                    break;
                case "ArrowRight":
                    if (observations && currentIndex + 1 !== _.get(observations, "length")) {
                        navigate(true);
                    }
                    break;
                case "[":
                    navigateAsset(false);
                    break;
                case "]":
                    navigateAsset(true);
                    break;
                default:
                    break;
            }
        },
        [editingFieldKey, editingFieldValue, currentIndex, observations, navigateAsset, navigate],
    );

    useEffect(() => {
        document.addEventListener("keyup", handleKeyPress, false);
        return () => {
            document.removeEventListener("keyup", handleKeyPress, false);
        };
    }, [handleKeyPress]);

    const overlayBbox = useMemo(() => {
        if (observation.bbox) {
            return [observation.bbox];
        } else {
            return [];
        }
    }, [observation.bbox]);

    const updateOcrFieldValue = (value) => {
        const regexError = validateGroundTruth(currentIssue.class, value);
        if (regexError) {
            setOcrOverrideError(regexError);
        } else {
            setOcrOverrideError("");
        }

        setOcrOverride(value);
    };

    const setCustomOcrValue = () => {
        const observation = orderedObservations[currentIndex];
        const classSpecificData = _.get(observation, "class_specific_data");
        classSpecificData["ocr_result_custom"] = AssetIDHelper.convert_slash_or_hyphen_to_csv(ocrOverride);
        dispatch(
            setAssetObservationClassification(observation, classSpecificData, (success) => {
                if (success) {
                    setOcrOverride("");
                    setEditingOcr(false);
                }
            }),
        );
    };

    useEffect(() => {
        if (editingOcr && ocrInputRef.current) {
            ocrInputRef.current.focus({ cursor: "end" });
        }
    }, [editingOcr]);

    const ocrResult = useMemo(() => {
        if (!superuser) {
            return null;
        }

        let ocrResult = _.get(observation, ["class_specific_data", "ocr_result_custom"], "");
        if (!ocrResult) {
            ocrResult = _.get(observation, ["class_specific_data", "ocr_result"]);
        }
        const formattedOcrResult = new AssetIDHelper(ocrResult).csv_to_slashes();
        if (!ocrResult) {
            return null;
        }

        let editIcon = faEdit;
        let onIconClick = () => {
            setEditingOcr(true);
            setOcrOverride(formattedOcrResult);
        };

        let valueElement = <p className="InfoText">{formattedOcrResult}</p>;

        if (editingOcr) {
            const isInvalid = !ocrOverride || ocrOverrideError;

            valueElement = (
                <Tooltip
                    visible={!!ocrOverrideError}
                    trigger="manual"
                    placement="bottomLeft"
                    title={<span style={{ color: "red" }}>{ocrOverrideError}</span>}>
                    <Input.Search
                        ref={ocrInputRef}
                        size="small"
                        onSearch={() => {
                            if (!isInvalid) {
                                setCustomOcrValue();
                            }
                        }}
                        className={`InfoValueInput${ocrOverrideError ? " has-error" : ""}`}
                        value={ocrOverride}
                        onChange={(e) => updateOcrFieldValue(e.target.value)}
                        enterButton={
                            <Button
                                disabled={isInvalid}
                                type="primary"
                                htmlType="submit">
                                Save
                            </Button>
                        }
                    />
                </Tooltip>
            );
            editIcon = faTimes;
            onIconClick = () => {
                setEditingOcr(false);
                setOcrOverride("");
                setOcrOverrideError("");
            };
        }

        return (
            <div className="InfoItem">
                <div className="TitleContainer">
                    <p className="Title">OCR Result</p>
                    <FontAwesomeIcon
                        className="EditIcon"
                        icon={editIcon}
                        onClick={onIconClick}
                    />
                </div>
                <div className="InfoValues">{valueElement}</div>
            </div>
        );
    }, [editingOcr, observation, ocrOverride, ocrOverrideError, superuser]);

    const vegetationState = useMemo(() => {
        if (!shouldShowVeg) return;

        const vegetation = _.get(observation, ["classifications", "vegetation_state", "auto"]);

        if (!(vegetation in ohlVegetationOptions)) return;

        return (
            <div
                className="InfoItem"
                key="vegetation">
                <div className="TitleContainer">
                    <p className="Title">Vegetation</p>
                </div>
                <div className="InfoValues">{ohlVegetationOptions[vegetation]}</div>
            </div>
        );
    }, [observation, shouldShowVeg]);

    const signalExtraData = useMemo(() => {
        if (_.isNil(currentIssue) || currentIssue.class !== "Signal") return;

        const classificationElements = [];
        const classificationTypes = _.get(objectObservationDataTypes, "Signal", {});

        Object.keys(classificationTypes).forEach((type) => {
            const isIsolationField = type.includes("isolation");

            if (isIsolationField && !_.get(currentDashboard, ["config", "show_essow_input"], false)) {
                return;
            }

            const classification = _.get(observation, ["classifications", type], null);

            let currentClassification = _.get(classification, "user", null);
            let statusIndicator;

            if (currentClassification !== null) {
                const userTs = _.get(classification, "user_ts", null);
                statusIndicator = (
                    <Tooltip
                        title={
                            <span>
                                Verified by <strong>{_.get(classification, "source", "Unknown")}</strong>
                                {convertToUtc !== null && userTs && ` on ${formatHumanDate(userTs, convertToUtc)}`}
                            </span>
                        }>
                        <div className="VerifiedContainer">
                            <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="VerifiedIcon"
                            />
                            <span>Verified</span>
                        </div>
                    </Tooltip>
                );
            } else {
                currentClassification = _.get(classification, "auto", null);

                if (currentClassification !== null) {
                    statusIndicator = (
                        <OBCAutoIndicator
                            tooltip="Click to verify"
                            placement="top"
                            onClick={() => {
                                dispatch(updateIssueObservationClassification(observation.id, type, currentClassification));
                            }}
                        />
                    );
                } else {
                    statusIndicator = null;
                }
            }

            const dataValues = _.get(classificationTypes, [type, "data_values"], {});
            const title = _.get(classificationTypes, [type, "display_name"], type);

            classificationElements.push(
                <div
                    className="InfoItem"
                    key={type}>
                    <div className="TitleContainer">
                        <p className="Title">
                            {type.includes("isolation") ? (
                                <Tooltip title="This data should not be relied upon for safety-critical decision making.">{title}*</Tooltip>
                            ) : (
                                title
                            )}
                        </p>
                        {statusIndicator}
                    </div>
                    <div class="InfoValues">
                        <Select
                            className="InfoValuesSelect"
                            size="small"
                            value={currentClassification ?? undefined}
                            placeholder={`Classify ${title}`}
                            style={{ width: 150 }}
                            onChange={(value) => {
                                dispatch(updateIssueObservationClassification(observation.id, type, value));
                            }}>
                            {Object.keys(dataValues).map((value) => {
                                if (typeof dataValues[value] === "string") {
                                    return <Select.Option value={dataValues[value]}>{value}</Select.Option>;
                                }

                                const displayName = _.get(dataValues[value], "display_name", "Unknown");
                                const ragStatus = _.get(dataValues[value], "rag");
                                const colours = {
                                    red: "#E55151",
                                    amber: "#F8C46D",
                                    green: "#47C66B",
                                };

                                return (
                                    <Select.Option value={value}>
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            {displayName}
                                            {ragStatus && (
                                                <span
                                                    role="img"
                                                    className="detectionCondition__Rag"
                                                    style={{
                                                        backgroundColor: colours[ragStatus],
                                                        height: "10px",
                                                        width: "10px",
                                                        borderRadius: "50%",
                                                        marginLeft: "5px",
                                                    }}
                                                />
                                            )}
                                        </div>
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </div>
                </div>,
            );
        });

        return classificationElements;
    }, [convertToUtc, currentDashboard, currentIssue, dispatch, objectObservationDataTypes, observation]);

    const toggleAssetReadable = () => {
        const unreadable = !currentIssue.category;
        dispatch(setAssetUnreadable(currentIssue.id, unreadable));
        setDisplayReadableModal(false);
        currentIssue.category = unreadable ? 1 : 0;
        setUnreadableUpdated(!unreadableUpdated);
    };

    const toggleImageOverlay = () => {
        setShowImageOverlay(!showImageOverlay);
    };

    const railInspectionButton = useMemo(() => {
        if (
            _.get(currentDashboard, ["config", "rail_inspection_enabled"], false) &&
            _.get(observation, "inspection_session", false) &&
            _.get(currentIssue, "class") === "Thermal Hotspot"
        ) {
            return (
                <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href={`/rail-inspection/${observation.session_id}/${observation.timestamp}`}>
                    <Button
                        style={{ margin: "0 5px" }}
                        type="primary"
                        key="open">
                        Open Rail Inspection
                    </Button>
                </a>
            );
        }
        return null;
    }, [currentDashboard, observation, currentIssue]);

    const strideImageData = useMemo(() => {
        if (!observation || !("stills_stride" in observation) || observation.stills_stride === -1 || !csrfToken) {
            return null;
        }

        const url = `https://raw.aivr.video/${observation.device_key}/${observation.session_key}/${observation.video_key}.stills.txt`;
        const startRange = observation.stills_stride * (observation.frame - 1);
        const endRange = startRange + observation.stills_stride - 1;

        return {
            url,
            range: { start: startRange, end: endRange },
            csrfToken,
        };
    }, [csrfToken, observation]);

    if (!currentIssue) {
        return (
            <Modal
                visible={true}
                width={"90%"}
                transitionName=""
                className="ObservationModal HideModalCancelButton"
                bodyStyle={{ height: "85vh", overflow: "hidden", padding: 15 }}>
                <div className="flex h-full justify-center items-center">
                    <div className="flex flex-col justify-center items-center">
                        <FontAwesomeIcon
                            icon={faTrain}
                            style={{ fontSize: 64 }}
                        />
                        <span
                            className="AbsoluteCentre"
                            style={{ zIndex: 1, fontSize: 48 }}>
                            {isIssue ? "Issue unavailable" : "Asset unavailable"}
                        </span>
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal
            visible={true}
            onOk={closeModal}
            onCancel={closeModal}
            width={"90%"}
            transitionName=""
            className="ObservationModal"
            bodyStyle={{ height: "85vh", overflow: "hidden", padding: 15 }}
            footer={
                <div className="ObservationModalFooter">
                    <div className="LeftContainer">
                        <PopoverButton
                            type="danger"
                            onClick={() => reviewObservation(4)}
                            buttonText={"Unmatch"}
                            disabled={loadingNextAsset}
                            popoverText={`This observation does not match with the others in this ${isIssue ? "issue" : "asset"}, it should be treated separately.`}
                        />

                        <PopoverButton
                            type="danger"
                            onClick={() => reviewObservation(3)}
                            buttonText={"Reject"}
                            disabled={loadingNextAsset}
                            popoverText={"This observation is incorrect. It should be completely ignored."}
                        />

                        {[88, 89].includes(observation.matching_state) && (
                            <PopoverButton
                                type="primary"
                                onClick={() => reviewObservation(5)}
                                buttonText={"Match"}
                                disabled={loadingNextAsset}
                                popoverText={`Match this observation to the current ${isIssue ? "issue" : "asset"}.`}
                            />
                        )}
                    </div>

                    <div className="RightContainer">
                        <Button
                            onClick={closeModal}
                            key="close">
                            Close
                        </Button>
                        {railInspectionButton}
                        <Popover
                            overlayClassName="PopoverContextMenuContainer"
                            trigger="contextMenu"
                            destroyTooltipOnHide
                            content={
                                <div className="PopoverContextMenu">
                                    <Button
                                        type="primary"
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(
                                                `${window.location.href}view/${observation.session_id}/${observation.timestamp}`,
                                                "_blank",
                                                "noreferrer",
                                            );
                                        }}>
                                        Open in New Tab
                                    </Button>
                                </div>
                            }>
                            <Button
                                type="primary"
                                onClick={openSession}
                                key="open">
                                Open Session
                            </Button>
                        </Popover>
                    </div>
                </div>
            }>
            <div
                className="ObservationModalContainer"
                id="observation-modal-container">
                <div className="Header">
                    <div className="TitleContainer">
                        <p className="TitleLabel">{isIssue ? "Issue ID" : "Asset ID"}</p>
                        <div className="TitleRow">
                            <Tooltip title={isIssue ? "Previous Issue" : "Previous Asset"}>
                                <FontAwesomeIcon
                                    onClick={() => goToAssetID(prevAssetData)}
                                    className={"AssetNavigationIcon Left" + (!prevAssetData || loadingNextAsset ? " Disabled" : "")}
                                    size="lg"
                                    icon={faArrowCircleLeft}
                                />
                            </Tooltip>
                            <div className="TitleValue">
                                <span className="muted">#</span>
                                <span className="select-text">{currentIssue.id}</span>
                            </div>
                            <Tooltip title={isIssue ? "Next Issue" : "Next Asset"}>
                                <FontAwesomeIcon
                                    onClick={() => goToAssetID(nextAssetData)}
                                    className={"AssetNavigationIcon Right" + (!nextAssetData || loadingNextAsset ? " Disabled" : "")}
                                    size="lg"
                                    icon={faArrowCircleRight}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div className="InfoItems">
                        {issueInfo}
                        {displayUnreadable && (
                            <Button
                                type="danger"
                                onClick={() => setDisplayReadableModal(!displayReadableModal)}>
                                {!currentIssue.category ? "Unreadable" : "Readable"}
                            </Button>
                        )}
                    </div>
                    {!!groundTruthError && <p className="GroundTruthError">{groundTruthError}</p>}
                    {_.get(observation, "reprocessing", false) && (
                        <div className="RecalculationMessage">
                            <span>Recalculating outputs. Data analysis is in progress and it can take several minutes to complete.</span>
                            <Button
                                size="small"
                                disabled={successfullyRefetchedObservations}
                                onClick={() => {
                                    dispatch(refreshAssetObservations(selectedAssetData.asset)).then(() => {
                                        setSuccessfullyRefetchedObservations(true);
                                        setTimeout(() => {
                                            setSuccessfullyRefetchedObservations(false);
                                        }, MINUTE_IN_MS);
                                    });
                                }}>
                                Reload observations
                            </Button>
                            {successfullyRefetchedObservations && (
                                <FontAwesomeIcon
                                    icon={faCheck}
                                    style={{ color: "green" }}
                                />
                            )}
                        </div>
                    )}
                </div>

                <Modal
                    visible={displayReadableModal}
                    onOk={toggleAssetReadable}
                    onCancel={() => setDisplayReadableModal(false)}>
                    <p style={{ margin: "0" }}>Mark this asset as {!currentIssue.category ? "unreadable" : "readable"}?</p>
                </Modal>

                {!loadingNextAsset && totalAssets > 0 && (
                    <div
                        className="ObservationInfoContainer"
                        id="observation-info-container">
                        <div
                            className="InfoItem"
                            key="current-item">
                            <div className="TitleContainer">
                                <p className="Title">Observation Item</p>
                            </div>
                            <div className="InfoValues">
                                <FontAwesomeIcon
                                    className="InfoIcon"
                                    icon={faSortNumericUp}
                                />
                                <p className="InfoText">
                                    {currentIndex + 1} of {orderedObservations.length}
                                </p>
                            </div>
                        </div>

                        <div className="InfoItem">
                            <div className="TitleContainer">
                                <p className="Title">Observation ID</p>
                            </div>
                            <div className="InfoValues">
                                <p className="InfoText">
                                    <span className="muted">#</span>
                                    <span className="select-text">{observation.id}</span>
                                </p>
                            </div>
                        </div>

                        <div className="InfoItem">
                            <div className="TitleContainer">
                                <p className="Title">Observed On</p>
                            </div>
                            <div
                                className="InfoValues"
                                style={{ width: 180 }}>
                                <p className="InfoText">{humanDate}</p>
                            </div>
                        </div>

                        <div className="InfoItem">
                            <div className="TitleContainer">
                                <p className="Title">Session ID</p>
                            </div>
                            <div className="InfoValues">
                                <p className="InfoText">
                                    <span className="muted">#</span>
                                    <span className="select-text">{observation.session_id}</span>
                                </p>
                            </div>
                        </div>

                        {ocrResult}
                        {vegetationState}
                        {signalExtraData}
                    </div>
                )}

                {!loadingNextAsset && (
                    <>
                        <Tooltip title="Previous Observation">
                            <FontAwesomeIcon
                                onClick={() => {
                                    navigate(false);
                                    setBrightness(0);
                                    setContrast(0);
                                }}
                                className={"NavigationIcon Left" + (currentIndex - 1 < 0 ? " Disabled" : "")}
                                icon={faArrowCircleLeft}
                            />
                        </Tooltip>

                        <Tooltip title="Next Observation">
                            <FontAwesomeIcon
                                onClick={() => {
                                    navigate(true);
                                    setBrightness(0);
                                    setContrast(0);
                                }}
                                className={"NavigationIcon Right" + (observations && currentIndex + 1 > observations.length - 1 ? " Disabled" : "")}
                                icon={faArrowCircleRight}
                            />
                        </Tooltip>
                    </>
                )}

                <div className="ObservationImageContainer">
                    {loadingNextAsset ? (
                        <OBCSpinner
                            className="ObservationMainSpinner"
                            size={100}
                            speed={3}
                            colorScheme="mono"
                        />
                    ) : (
                        <ZoomableImage
                            filters={{ brightness, contrast }}
                            zoom={zoom}
                            overlayBoxes={!observation.source_image_url ? overlayBbox : []} //do not draw bbox if obsercation has source_image_url
                            imgSrc={formatImage(observation.source_image_url && !showImageOverlay ? observation.source_image_url : observation.image_url)}
                            strideImageData={!showImageOverlay ? strideImageData : null}
                            withSpinner={true}
                            updateZoom={setZoom}
                            showOverlayContent={showImageOverlay}
                            frame={null}
                            videoKey={observation.video_key}
                        />
                    )}
                </div>
                <div className="SettingsContainer">
                    <div className="OptionContainer">
                        <h4>Zoom</h4>
                        <Slider
                            tooltipVisible={false}
                            step={0.1}
                            min={1}
                            max={20}
                            onChange={setZoom}
                            disabled={loadingNextAsset}
                            value={zoom}
                        />
                    </div>

                    <div className="OptionContainer">
                        <div className="obsModalOptionContainer">
                            <h4>Brightness</h4>
                            <Tooltip title="Reset Brightness">
                                <FontAwesomeIcon
                                    className="ResetButton"
                                    icon={faRedo}
                                    onClick={() => setBrightness(0)}
                                    size="sm"
                                    style={{ cursor: "pointer", margin: "5px", transform: "scaleX(-1)" }}
                                />
                            </Tooltip>
                        </div>
                        <Slider
                            tooltipVisible={false}
                            step={0.1}
                            min={-1}
                            max={1}
                            onChange={(val) => setBrightness(val)}
                            disabled={loadingNextAsset}
                            value={brightness}
                        />
                    </div>

                    <div className="OptionContainer">
                        <div className="obsModalOptionContainer">
                            <h4>Contrast</h4>
                            <Tooltip title="Reset Contrast">
                                <FontAwesomeIcon
                                    className="ResetButton"
                                    icon={faRedo}
                                    onClick={() => setContrast(0)}
                                    size="sm"
                                    style={{ cursor: "pointer", margin: "5px", transform: "scaleX(-1)" }}
                                />
                            </Tooltip>
                        </div>
                        <Slider
                            tooltipVisible={false}
                            step={0.1}
                            min={-1}
                            max={1}
                            onChange={(val) => setContrast(val)}
                            disabled={loadingNextAsset}
                            value={contrast}
                        />
                    </div>
                    {observation.source_image_url && (
                        <div
                            className="InfoItem"
                            key="overlay">
                            <div className="TitleContainer">
                                <h4>Show Overlay</h4>
                            </div>
                            <div className="InfoValues">
                                <Switch
                                    defaultChecked
                                    size="small"
                                    checked={showImageOverlay}
                                    onChange={toggleImageOverlay}
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default ObservationModal;
